import { Icon } from '../ui/Icon'
import { Sheet } from '../ui/Sheet'
import { type Clash } from './useFsActions'

/**
 * "That name is taken — replace it, or keep both?"
 *
 * Owner, 2026-09-14, relaying a user's report: 比如我上传了一个文件A，然后我本地修改了
 * 文件A我再上传的时候就会和老的文件撞了，但我其实是想覆盖而不是生成一个 A_1.
 *
 * Note what that report is actually about. Keeping both was never a missing feature —
 * it is what the edge has always done, deliberately, because an upload that silently
 * replaced a file would be data loss reported as success. The complaint is that it was
 * the ONLY answer, and re-uploading an edited file is the commonest reason to upload a
 * file at all. So this sheet does not change the rule; it asks the question the rule
 * was standing in for, and replacing stays something a person has to say out loud.
 *
 * Replace is offered only for names taken by a FILE. A file cannot replace a folder —
 * the edge refuses it — so a folder clash shows as a plain statement of what will
 * happen instead of a button whose only outcome is an error, the same way the context
 * menu omits "Download file" on a folder rather than disabling it.
 */
export function UploadConflictSheet({
  clashes,
  host,
  onCancel,
  onKeepBoth,
  onReplace,
}: {
  clashes: Clash[]
  /** Mounted remote this will run on; null = Origin. */
  host: string | null
  onCancel: () => void
  onKeepBoth: () => void
  onReplace: () => void
}) {
  const files = clashes.filter((c) => !c.isDir)
  const folders = clashes.filter((c) => c.isDir)
  const one = clashes.length === 1

  return (
    <Sheet
      title={one ? `${clashes[0].existing} already exists` : `${clashes.length} names already exist`}
      onClose={onCancel}
      actions={
        <>
          <button type="button" className="sheet-btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="sheet-btn" onClick={onKeepBoth}>
            Keep both
          </button>
          {files.length > 0 && (
            <button type="button" className="sheet-btn danger" onClick={onReplace}>
              {files.length === 1 ? 'Replace' : `Replace ${files.length}`}
            </button>
          )}
        </>
      }
    >
      <div className="gittarget">
        <span>{host ? `on ${host}` : 'on Origin (this machine)'}</span>
      </div>

      {files.length > 0 && (
        <div className="gitconfirm-grp">
          <span>
            Keep both uploads {one ? 'this as' : 'these as'} “name (1)”. Replace overwrites{' '}
            {one ? 'the file that is there' : 'the files that are there'} — this cannot be undone.
          </span>
          <ul>
            {files.map((c) => (
              <li key={c.name}>
                {c.existing}
                {/* The two spellings differ only on a case-insensitive machine, and
                    there it is the whole reason this counted as a collision. Saying
                    both is how the person can tell that is what happened. */}
                {c.existing !== c.name && <em> — uploading as {c.name}</em>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {folders.length > 0 && (
        <div className="gitconfirm-grp">
          <span>
            <Icon name="warning" size={15} /> A folder has{' '}
            {folders.length === 1 ? 'this name' : 'these names'} — a file cannot replace a
            folder, so {folders.length === 1 ? 'it uploads' : 'they upload'} as “name (1)”
            either way.
          </span>
          <ul>
            {folders.map((c) => (
              <li key={c.name}>{c.existing}</li>
            ))}
          </ul>
        </div>
      )}
    </Sheet>
  )
}
